import { indentWithTab } from "@codemirror/commands";
import { StreamLanguage } from "@codemirror/language";
import { csharp } from "@codemirror/legacy-modes/mode/clike";
import { css, less, sCSS } from "@codemirror/legacy-modes/mode/css";
import { javascript } from "@codemirror/legacy-modes/mode/javascript";
import { html, xml } from "@codemirror/legacy-modes/mode/xml";
import { Extension } from "@codemirror/state";
import { keymap } from "@codemirror/view";
import { EditorView, basicSetup } from "codemirror";
import { Assert } from "../../Assert";
import { DummyEvent, Nullable, PartialNullable } from "../../Extensions";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property } from "../../RadObject";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { NAMESPACE } from "./_Namespace";

import "./SourceCode.scss";

const
    languageCSharp = StreamLanguage.define(csharp),
    languageCss = StreamLanguage.define(css),
    languageHtml = StreamLanguage.define(html),
    languageJavascript = StreamLanguage.define(javascript),
    languageLess = StreamLanguage.define(less),
    languageScss = StreamLanguage.define(sCSS),
    languageXml = StreamLanguage.define(xml);

export enum SourceCodeModeEnum
{
    csharp = "text/x-csharp",
    css = "text/css",
    html = "text/html",
    less = "text/x-less",
    javascript = "text/javascript",
    scss = "text/x-scss",
    xml = "text/xml",
}

export type SourceCodePropertyValues =
    InputControlPropertyValues
    & {
        mode: SourceCodeModeEnum;
    };

export type SourceCodeProperties = {
    [name in keyof SourceCodePropertyValues]: Property<SourceCodePropertyValues[name]>
};

export interface ISourceCode extends IInputControl, SourceCodeProperties
{
}

export class SourceCode extends InputControl implements ISourceCode
{
    // #region Static

    public static override classNamespace = NAMESPACE + "SourceCode";
    public static override className = "SourceCode";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<SourceCodePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                mode: SourceCodeModeEnum.csharp,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected __elementValueChanging?: boolean;

    protected $$handle?: JNative<HTMLElement>;
    protected $$textArea?: JNative<HTMLTextAreaElement>;
    protected editor?: EditorView;
    protected resizeInfo?: { startHeight: number, startY: number };

    // #endregion

    // #region Properties

    public mode!: Property<SourceCodeModeEnum>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<SourceCodePropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-source-code");

        // Trigger Property Changes
        this.mode.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _elementValueChanged()
    {
        this.__elementValueChanging = true;
        this.value.set(this._elementValueGet());
        this.__elementValueChanging = false;
    }
    protected _elementValueGet()
    {
        return this.$$textArea?.val();
    }
    protected _elementValueSet(value: any)
    {
        const
            editor = this.editor;

        if (!editor)
            return;

        editor.dispatch({ changes: [{ from: 0, to: editor.state.doc.length, insert: !ObjectHelper.isNullOrUndefined(value) ? value : StringHelper.empty }] })
    }
    protected _modeChanged(change: PropertyChange<SourceCodeModeEnum>)
    {
        this.createEditor();
    }
    protected _getCodeMirrorExtensions(mode: Nullable<SourceCodeModeEnum>): Extension[]
    {
        const
            extensions: Extension[] = [basicSetup],
            languageOptions = this._getCodeMirrorLanguageOptions(mode);

        switch (mode)
        {
            case SourceCodeModeEnum.csharp:
                if (languageOptions)
                    extensions.push(languageCSharp.data.of(languageOptions));
                extensions.push(languageCSharp);
                break;
            case SourceCodeModeEnum.css:
                extensions.push(languageCss);
                break;
            case SourceCodeModeEnum.html:
                extensions.push(languageHtml);
                break;
            case SourceCodeModeEnum.javascript:
                extensions.push(languageJavascript);
                break;
            case SourceCodeModeEnum.less:
                extensions.push(languageLess);
                break;
            case SourceCodeModeEnum.scss:
                extensions.push(languageScss);
                break;
            case SourceCodeModeEnum.xml:
                extensions.push(languageXml);
                break;
            default:
                throw new Error(`Mode ${mode} has not been implemented`);
        }

        extensions.push(keymap.of([indentWithTab]));

        extensions.push(
            EditorView.updateListener.of((update) =>
            {
                if (update.docChanged)
                    this.onEditorChanged(new DummyEvent(this), { editor: this.editor });
            })
        );

        return extensions;
    }
    protected _getCodeMirrorLanguageOptions(mode: Nullable<SourceCodeModeEnum>): { [name: string]: any }
    {
        switch (mode)
        {
            case SourceCodeModeEnum.csharp:
            case SourceCodeModeEnum.css:
            case SourceCodeModeEnum.html:
            case SourceCodeModeEnum.javascript:
            case SourceCodeModeEnum.less:
            case SourceCodeModeEnum.scss:
            case SourceCodeModeEnum.xml:
                return {};
            default:
                throw new Error(`Mode ${mode} has not been implemented`);
        }
    }

    // #endregion

    // #region Private Methods

    private createEditor()
    {
        if (!this.$$textArea)
            return;

        this.destroyEditor();

        const
            extensions = this._getCodeMirrorExtensions(this.mode.get())

        this.editor =
            new EditorView({
                doc: this.$$textArea.val(),
                extensions: extensions,
            });

        $$(this.editor.dom).insertBefore(this.$$textArea);
        this.$$textArea.css("display", "none");

        this.triggerHandler("editorCreated", this.editor);
    }
    private destroyEditor()
    {
        if (!this.editor)
            return;

        if (this.$$textArea)
        {
            this.$$textArea.val(this.editor.state.doc.toString());
            this.$$textArea.css("display", StringHelper.empty);
        }

        this.editor.destroy();
        delete this.editor;
    }
    private resizeEnd()
    {
        $$("body").off("mousemove", { self: this }, this.onBodyMouseMove);
        $$(window).off("mouseup", { self: this }, this.onWindowMouseUp);
    }
    private resizeStart(startY: number)
    {
        if (!this.editor)
            return;

        const
            $$element = this.$$element.get();

        Assert.isNotNullOrUndefined($$element, "$$element");

        this.resizeInfo = {
            startY,
            startHeight: $$element.find(".cm-editor").outerHeight()
        };

        $$("body").on("mousemove", { self: this }, this.onBodyMouseMove);
        $$(window).on("mouseup", { self: this }, this.onWindowMouseUp);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: SourceCodePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.mode = new Property<SourceCodeModeEnum>(this, "System.String, System.Private.CoreLib", propertyValues.mode, this._modeChanged);

        this.value.overrideSet((value, next) =>
        {
            if (!this.__elementValueChanging)
                this._elementValueSet(value);

            next(value);
        });
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this.$$handle, "this.$$handle");

        this.on("keyup", { self: this }, this.onKeyUp);

        this.$$handle.on("mousedown", { self: this }, this.onHandleElementMouseDown);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.$$textArea = JNativeStatic.create$$("textarea") as JNative<HTMLTextAreaElement>;
        this.$$textArea.appendTo($$element);

        this.$$handle =
            JNativeStatic.create$$({
                nodeName: "div",
                attributes: { "class": "rad-handle" }
            }).appendTo($$element);

        return $$element;
    }
    protected override _dispose()
    {
        this.destroyEditor();
        super._dispose();
    }

    // #endregion

    // #region Event Handlers

    protected override onAncestorVisibilityChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        self.editor?.requestMeasure();
    }
    protected onBodyMouseMove(evt: IJNativeEvent<MouseEvent>)
    {
        const
            self = evt.data.self as this;

        Assert.isNotNullOrUndefined(self.resizeInfo, "self.resizeInfo");

        if (self.editor)
            self.editor.dom.style.height = Math.max(100, (self.resizeInfo.startHeight + evt.originalEvent.y - self.resizeInfo.startY)) + "px";
    }
    protected onEditorChanged(evt: IJNativeEvent, eventData: any)
    {
        const
            self = evt.data.self as this;

        if (self.$$textArea && self.editor)
            self.$$textArea.val(self.editor.state.doc.toString());

        self._elementValueChanged();
    }
    protected onHandleElementMouseDown(evt: IJNativeEvent<MouseEvent>)
    {
        const
            self = evt.data.self as this;

        self.resizeStart(evt.originalEvent.y);
    }
    protected onHandleElementMouseUp(evt: IJNativeEvent<MouseEvent>)
    {
        const
            self = evt.data.self as this;

        self.resizeEnd();
    }
    protected onKeyUp(evt: IJNativeEvent<KeyboardEvent>)
    {
        switch (evt.originalEvent.key)
        {
            case "Enter":
            case "Escape":
                evt.stopPropagation();
                break;
        }
    }
    protected override onPageActivated(evt: IJNativeEvent, eventData: any)
    {
        const
            self = evt.data.self as this;

        window.setTimeout(() => self.editor?.requestMeasure(), 50);
    }
    protected onWindowMouseUp(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.resizeEnd();
    }

    // #endregion

    // #endregion
}
